import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useKeenSlider } from "keen-slider/react";
import { VFC } from "react";
import styled from "styled-components";
import { useAnchor } from "hooks";
import { theme } from "styles";
import { Carousel5Content } from "types/graphql/generated";
import { Employee } from "./components";

interface Props {
  content: Carousel5Content;
}

const Carousel5: VFC<Props> = withErrorBoundary(({ content: { title, carousel5Team } }) => {
  const [ref] = useKeenSlider<HTMLDivElement>({
    slides: {
      perView: 1.21,
      spacing: 16,
    },
    breakpoints: {
      "(min-width: 450px)": {
        slides: {
          perView: 1.5,
          spacing: 16,
        },
      },
      [`(min-width: ${theme.breakpoints.sm + 1}px)`]: {
        disabled: true,
      },
    },
  });

  useAnchor("carousel5");

  return (
    <StyledCarousel5 className="carousel5 Content">
      <div className="Carousel5__backgroundContainer sm:FullBleed">
        <h2 className="Carousel5__title Font_48_54_600 lg:Font_40_48_600">{title}</h2>

        <div className="Carousel5__desktopTeam">
          {carousel5Team.map((employee) => (
            <Employee key={employee.id} className="Carousel5__imageContainer" content={employee} />
          ))}
        </div>

        <div className="Carousel5__mobileTeam Content">
          <div ref={ref} className="keen-slider">
            {carousel5Team.map((employee) => (
              <div key={employee.id} className="Carousel5__imageContainer keen-slider__slide">
                <Employee content={employee} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </StyledCarousel5>
  );
}, {});

const StyledCarousel5 = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Carousel5__backgroundContainer {
    background-size: 100% 100%;
    padding: 120px 106px;
    /* stylelint-disable-next-line declaration-block-no-shorthand-property-overrides */
    background: url("/images/noise.png"),
      radial-gradient(
        92.25% 102.37% at 108.46% 114.67%,
        rgb(7 64 78 / 75%) 0%,
        rgb(192 221 214 / 75%) 59.75%,
        rgb(246 243 241 / 0%) 100%
      ),
      #faf7f4;
    background-blend-mode: overlay, normal, normal;
    background-repeat: no-repeat;
  }

  @media (min-width: 1200px) {
    .Carousel5__backgroundContainer {
      background-size: 100% 100%;
    }
  }

  .Carousel5__title {
    padding-bottom: 24px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.fog["80"]};
  }

  .Carousel5__desktopTeam {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 40px 32px;
    padding-top: 60px;
  }

  .Carousel5__mobileTeam {
    display: none;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Carousel5__backgroundContainer {
      padding: 120px 54px;
    }

    .Carousel5__desktopTeam {
      grid-gap: 32px 24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Carousel5__backgroundContainer {
      padding: 64px 28px;
    }

    .Carousel5__desktopTeam {
      grid-template-columns: repeat(3, 1fr);
      grid-gap: 24px 16px;
      padding-top: 40px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Carousel5__desktopTeam {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Carousel5__backgroundContainer {
      padding: 40px 0;
    }

    .Carousel5__title {
      padding-left: 24px;
      padding-right: 24px;
      padding-bottom: 0;
      border-bottom: none;
    }

    .Carousel5__desktopTeam {
      display: none;
    }

    .Carousel5__mobileTeam {
      display: flex;
      margin-top: 40px;
    }

    .keen-slider {
      overflow: visible;
    }
  }
`;

gql`
  fragment Carousel5Content on ComponentCarouselCarousel5 {
    title
    carousel5Team {
      ...Carousel5EmployeeContent
    }
  }
`;

export { Carousel5 };
export type { Props as Carousel5Props };
